/**
 * SH Before After Web Component
 * Handles video play/pause functionality and Glide.js integration
 */

// Only define if not already exists (prevents duplicate declaration errors)
if (!window.ShBeforeAfter) {

  // Debug mode - set to true to enable console logging
  const SH_BEFORE_AFTER_DEBUG = false;
  const debugLog = (...args) => { if (SH_BEFORE_AFTER_DEBUG) console.log(...args); };
  const debugError = (...args) => { if (SH_BEFORE_AFTER_DEBUG) console.error(...args); };
  const debugWarn = (...args) => { if (SH_BEFORE_AFTER_DEBUG) console.warn(...args); };

  window.ShBeforeAfter = class extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.glideInstance = null;
      this.innerGlideInstances = new Map(); // Store inner slider instances
      this.videos = [];
      this.playButtons = [];
      this.pauseButtons = [];
      this.isInitialized = false;
    }
  
    connectedCallback() {
      // Get settings from data attributes
      this.sectionId = this.dataset.sectionId;
      
      // Setup event-based control system
      this.setupEventBasedControls();
      
      // Wait for EventBus and Glide to be available
      this.waitForGlideInitialization();
      
      // Fallback: Trigger EventBus interaction after delay if events haven't fired
      this.fallbackTimer = setTimeout(() => {
        if (!this.isInitialized) {
          debugLog(`Fallback - triggering EventBus page:interaction for before-after section ${this.sectionId}`);
          if (window.EventBus) {
            window.EventBus.emit('page:interaction');
          }
        }
      }, 3000); // 3 second safety net
    }
  
    disconnectedCallback() {
      // Cleanup when component is removed
      if (this.glideInstance) {
        this.glideInstance.destroy();
      }
      
      // Cleanup inner sliders
      this.destroyInnerSliders();
      
      // Cleanup video event listeners
      this.cleanupVideoEventListeners();
      
      // Clear fallback timer
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
      }
    }
  
    waitForGlideInitialization() {
      if (window.EventBus) {
        // Listen for interaction events (first time initialization)
        window.EventBus.on('page:interaction', () => {
          this.initializeCarousel();
        });
        
        // Listen for page change events (reinitialize)
        window.EventBus.on('page:change', () => {
          this.reinitializeCarousel();
        });
        
        // Also listen for direct glide:loaded events
        window.EventBus.on('glide:loaded', () => {
          this.initializeCarousel();
        });
      } else {
        // Fallback: wait for EventBus to be available
        const checkEventBus = () => {
          if (window.EventBus) {
            window.EventBus.on('page:interaction', () => {
              this.initializeCarousel();
            });
            window.EventBus.on('page:change', () => {
              this.reinitializeCarousel();
            });
            window.EventBus.on('glide:loaded', () => {
              this.initializeCarousel();
            });
          } else {
            setTimeout(checkEventBus, 100);
          }
        };
        checkEventBus();
      }
    }
  
    initVideoControls() {
      // Find all videos and their corresponding buttons
      this.videos = this.querySelectorAll('video');
      this.playButtons = this.querySelectorAll('.js-sh-play-video');
      this.pauseButtons = this.querySelectorAll('.js-sh-pause-video');
  
      // Add event listeners to play buttons
      this.playButtons.forEach((button, index) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.playVideo(index);
        });
      });
  
      // Add event listeners to pause buttons
      this.pauseButtons.forEach((button, index) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.pauseVideo(index);
        });
      });
  
      // Add event listeners to videos for state management
      this.videos.forEach((video, index) => {
        video.addEventListener('play', () => {
          this.onVideoPlay(index);
        });
  
        video.addEventListener('pause', () => {
          this.onVideoPause(index);
        });
  
        video.addEventListener('ended', () => {
          this.onVideoEnded(index);
        });
  
        // Pause video when it's not visible (optional)
        video.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this.pauseVideo(index);
          }
        });
      });
    }
  
    initializeCarousel() {
      if (this.isInitialized) {
        debugLog(`Before-after section ${this.sectionId} already initialized, skipping`);
        return;
      }

      debugLog(`Initializing before-after carousel for section ${this.sectionId}`);

      // Check if Glide is available before trying to use it
      if (typeof Glide === 'undefined') {
        debugLog(`Glide not loaded yet for before-after section ${this.sectionId}, waiting...`);
        return; // Exit gracefully, will retry when Glide loads
      }

      debugLog(`Glide is available`);

      const glideSelector = `.js-sh-before-after-slide-${this.sectionId}`;
      const glideElement = this.querySelector(glideSelector);

      if (!glideElement) {
        debugError(`Glide element not found with selector: ${glideSelector}`);
        debugLog(`Available elements in component:`, this.querySelectorAll('[class*="glide"]'));
        return;
      }

      debugLog(`Found glide element:`, glideElement);
  
      // Initialize video controls first
      this.initVideoControls();
  
      // Initialize inner image sliders first
      this.initInnerImageSliders();
  
      debugLog(`Creating Glide instance with selector: ${glideSelector}`);

      // Initialize Glide instance
      try {
        this.glideInstance = new Glide(glideSelector, {
        type: 'carousel',
        perView: 1,
        startAt: 0,
        gap: 20,
        autoplay: false,
        hoverpause: true,
        keyboard: true,
        dragThreshold: 80,
        swipeThreshold: 80,
        animationDuration: 400,
        animationTimingFunc: 'ease-in-out',
        breakpoints: {
          768: {
            perView: 1,
            gap: 10
          }
        }
      });

        // Setup Glide event listeners
        this.setupGlideEventListeners();

        // Mount Glide
        debugLog(`Mounting Glide instance...`);
        this.glideInstance.mount();

        this.isInitialized = true;

        debugLog(`Before-after carousel ${this.sectionId} initialized successfully!`);

        // Clear fallback timer since initialization was successful
        if (this.fallbackTimer) {
          clearTimeout(this.fallbackTimer);
          this.fallbackTimer = null;
        }
      } catch (error) {
        debugError(`Error initializing Glide for section ${this.sectionId}:`, error);
        debugError(`Error stack:`, error.stack);
      }
    }
  
    /**
     * Force reinitialize the carousel (useful for page changes)
     */
    reinitializeCarousel() {
      // Destroy existing instance
      if (this.glideInstance) {
        this.glideInstance.destroy();
        this.glideInstance = null;
      }
      
      // Destroy inner sliders
      this.destroyInnerSliders();
      
      // Reset initialization flag
      this.isInitialized = false;
      
      // Reinitialize
      this.initializeCarousel();
    }
  
    setupGlideEventListeners() {
      if (!this.glideInstance) return;
  
      // Before slide transition starts
      this.glideInstance.on('run.before', () => {
        this.pauseAllVideos();
      });
  
      // After slide transition completes
      this.glideInstance.on('run.after', () => {
        // Initialize any inner sliders in the new active slide that haven't been initialized yet
        this.initInnerImageSlidersInActiveSlide();
        
        // Force update all inner sliders in the active slide to recalculate dimensions
        this.updateInnerSlidersInActiveSlide();
      });
      
      // Also initialize inner sliders after mount
      this.glideInstance.on('mount.after', () => {
        this.initInnerImageSlidersInActiveSlide();
        // Small delay to ensure DOM is fully rendered
        setTimeout(() => {
          this.updateInnerSlidersInActiveSlide();
        }, 50);
      });
    }
  
    /**
     * Initialize all inner image sliders
     */
    initInnerImageSliders() {
      // Find all inner image slider elements
      const innerSliders = this.querySelectorAll('[data-inner-images-selector]');
      
      innerSliders.forEach((sliderElement) => {
        const selector = sliderElement.dataset.innerImagesSelector;
        
        if (!selector) {
          debugWarn('Inner slider element missing data-inner-images-selector');
          return;
        }
        
        // Skip if already initialized
        if (this.innerGlideInstances.has(selector)) {
          debugLog(`Inner slider already initialized, skipping: ${selector}`);
          return;
        }
        
        try {
          // Create Glide instance for inner slider
          const innerGlide = new Glide(`.${selector}`, {
            type: 'carousel',
            perView: 1,
            startAt: 0,
            gap: 0,
            autoplay: false,
            hoverpause: true,
            keyboard: false, // Disable keyboard to avoid conflicts with outer slider
            dragThreshold: 60,
            swipeThreshold: 60,
            animationDuration: 300,
            animationTimingFunc: 'ease-in-out'
          });
          
          // Prevent inner slider events from bubbling to outer slider
          this.preventEventBubbling(sliderElement);
          
          // Mount the inner slider
          innerGlide.mount();
          
          // Store the instance for later cleanup
          this.innerGlideInstances.set(selector, innerGlide);
          
          // Add custom bullet navigation and sync functionality
          this.addCustomBulletNavigation(sliderElement, innerGlide);
          this.syncBulletActiveState(sliderElement, innerGlide);
          
          debugLog(`Inner slider initialized with custom navigation: ${selector}`);
          
          // Force update dimensions after a brief delay to ensure visibility
          setTimeout(() => {
            try {
              innerGlide.update();
              debugLog(`Forced dimension update for: ${selector}`);
            } catch (updateError) {
              debugWarn(`Could not force update: ${selector}`, updateError);
            }
          }, 100);
        } catch (error) {
          debugError(`Error initializing inner slider ${selector}:`, error);
        }
      });
    }
  
    /**
     * Initialize inner sliders only in the currently active slide
     */
    initInnerImageSlidersInActiveSlide() {
      if (!this.glideInstance) {
        debugWarn('Cannot initialize inner sliders: main glide instance not available');
        return;
      }
  
      // Get the active slide element
      const activeIndex = this.glideInstance.index;
      const slides = this.querySelectorAll('.glide__slide');
      
      if (!slides || !slides[activeIndex]) {
        debugWarn(`Active slide not found at index ${activeIndex}`);
        return;
      }
  
      const activeSlide = slides[activeIndex];
      
      // Find inner sliders only in the active slide
      const innerSlidersInActiveSlide = activeSlide.querySelectorAll('[data-inner-images-selector]');
      
      debugLog(`Checking active slide ${activeIndex} for inner sliders, found: ${innerSlidersInActiveSlide.length}`);
      
      innerSlidersInActiveSlide.forEach((sliderElement) => {
        const selector = sliderElement.dataset.innerImagesSelector;
        
        if (!selector) {
          debugWarn('Inner slider element missing data-inner-images-selector');
          return;
        }
        
        // Skip if already initialized
        if (this.innerGlideInstances.has(selector)) {
          debugLog(`Inner slider already initialized, skipping: ${selector}`);
          return;
        }
        
        try {
          // Create Glide instance for inner slider
          const innerGlide = new Glide(`.${selector}`, {
            type: 'carousel',
            perView: 1,
            startAt: 0,
            gap: 0,
            autoplay: false,
            hoverpause: true,
            keyboard: false, // Disable keyboard to avoid conflicts with outer slider
            dragThreshold: 60,
            swipeThreshold: 60,
            animationDuration: 300,
            animationTimingFunc: 'ease-in-out'
          });
          
          // Prevent inner slider events from bubbling to outer slider
          this.preventEventBubbling(sliderElement);
          
          // Mount the inner slider
          innerGlide.mount();
          
          // Store the instance for later cleanup
          this.innerGlideInstances.set(selector, innerGlide);
          
          // Add custom bullet navigation and sync functionality
          this.addCustomBulletNavigation(sliderElement, innerGlide);
          this.syncBulletActiveState(sliderElement, innerGlide);
          
          debugLog(`Inner slider initialized in active slide ${activeIndex}: ${selector}`);
          
          // Force update dimensions after a brief delay to ensure parent slide is fully visible
          setTimeout(() => {
            try {
              innerGlide.update();
              debugLog(`Forced dimension update for: ${selector}`);
            } catch (updateError) {
              debugWarn(`Could not force update: ${selector}`, updateError);
            }
          }, 100);
        } catch (error) {
          debugError(`Error initializing inner slider ${selector}:`, error);
        }
      });
    }
  
    /**
     * Force update inner sliders in the active slide to recalculate dimensions
     * This fixes the issue where sliders are initialized but not visible, causing white screens
     */
    updateInnerSlidersInActiveSlide() {
      if (!this.glideInstance) {
        debugWarn('Cannot update inner sliders: main glide instance not available');
        return;
      }
  
      // Get the active slide element
      const activeIndex = this.glideInstance.index;
      const slides = this.querySelectorAll('.glide__slide');
      
      if (!slides || !slides[activeIndex]) {
        debugWarn(`Active slide not found at index ${activeIndex} for update`);
        return;
      }
  
      const activeSlide = slides[activeIndex];
      
      // Find inner sliders only in the active slide
      const innerSlidersInActiveSlide = activeSlide.querySelectorAll('[data-inner-images-selector]');
      
      if (innerSlidersInActiveSlide.length === 0) {
        debugLog(`No inner sliders found in active slide ${activeIndex}`);
        return;
      }
      
      debugLog(`Updating ${innerSlidersInActiveSlide.length} inner slider(s) in active slide ${activeIndex}`);
      
      innerSlidersInActiveSlide.forEach((sliderElement) => {
        const selector = sliderElement.dataset.innerImagesSelector;
        
        if (!selector) {
          return;
        }
        
        // Get the inner slider instance
        const innerGlide = this.innerGlideInstances.get(selector);
        
        if (innerGlide) {
          try {
            // Force Glide to recalculate dimensions
            innerGlide.update();
            debugLog(`Inner slider dimensions updated: ${selector}`);
          } catch (error) {
            debugError(`Error updating inner slider ${selector}:`, error);
          }
        } else {
          debugWarn(`Inner slider instance not found for update: ${selector}`);
        }
      });
    }
  
    /**
     * Prevent touch/mouse events from bubbling from inner slider to outer slider
     */
    preventEventBubbling(sliderElement) {
      const events = [
        'touchstart', 'touchmove', 'touchend',
        'mousedown', 'mousemove', 'mouseup',
        'pointerdown', 'pointermove', 'pointerup'
      ];
      
      events.forEach(eventType => {
        sliderElement.addEventListener(eventType, (e) => {
          // Stop the event from reaching the outer slider
          e.stopPropagation();
        }, { passive: false });
      });
      
      // Also prevent clicks from bubbling
      sliderElement.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
  
    /**
     * Add custom bullet and arrow navigation using Glide.js API
     */
    addCustomBulletNavigation(sliderElement, innerGlide) {
      // Set up click event listener on the entire inner slider container
      sliderElement.addEventListener('click', (e) => {
        e.stopPropagation(); // Isolate from outer slider
        
        // Handle bullet clicks
        if (e.target.classList.contains('sh-section-ba__sis--bullet')) {
          e.preventDefault();
          
          // Get slide index from data attribute
          const slideIndex = e.target.getAttribute('data-inner-images-index');
          
          // Navigate using Glide.js API
          if (innerGlide && slideIndex !== null && slideIndex !== '') {
            try {
              innerGlide.go(`=${slideIndex}`);
              debugLog(`Bullet navigation to slide ${slideIndex}`);
            } catch (error) {
              debugError('Inner slider bullet navigation error:', error);
            }
          }
        }
        
        // Handle arrow clicks
        if (e.target.classList.contains('sh-section-ba__nav--btn') && e.target.classList.contains('inner')) {
          e.preventDefault();
          
          // Get direction from data attribute
          const direction = e.target.getAttribute('data-inner-images-dir');
          if (direction && innerGlide) {
            try {
              innerGlide.go(direction); // '<' or '>'
              debugLog(`Arrow navigation: ${direction === '<' ? 'previous' : 'next'}`);
            } catch (error) {
              debugError('Inner slider arrow navigation error:', error);
            }
          }
        }
      });
  
      // Also handle touch events for mobile (both bullets and arrows)
      sliderElement.addEventListener('touchend', (e) => {
        e.stopPropagation();
        
        // Handle bullet touches
        if (e.target.classList.contains('sh-section-ba__sis--bullet')) {
          e.preventDefault();
          
          const slideIndex = e.target.getAttribute('data-inner-images-index');
          
          if (innerGlide && slideIndex !== null && slideIndex !== '') {
            try {
              innerGlide.go(`=${slideIndex}`);
              debugLog(`Touch bullet navigation to slide ${slideIndex}`);
            } catch (error) {
              debugError('Inner slider touch bullet navigation error:', error);
            }
          }
        }
        
        // Handle arrow touches
        if (e.target.classList.contains('sh-section-ba__nav--btn') && e.target.classList.contains('inner')) {
          e.preventDefault();
          
          const direction = e.target.getAttribute('data-inner-images-dir');
          if (direction && innerGlide) {
            try {
              innerGlide.go(direction);
              debugLog(`Touch arrow navigation: ${direction === '<' ? 'previous' : 'next'}`);
            } catch (error) {
              debugError('Inner slider touch arrow navigation error:', error);
            }
          }
        }
      }, { passive: false });
  
      debugLog(`Custom bullet and arrow navigation added for inner slider`);
    }
  
    /**
     * Sync bullet active state with current slide
     */
    syncBulletActiveState(sliderElement, innerGlide) {
      const bullets = sliderElement.querySelectorAll('.sh-section-ba__sis--bullet');
      if (bullets.length === 0) {
        debugWarn('No bullets found for active state sync');
        return;
      }
      
      // Function to update active bullet
      const updateActiveBullet = () => {
        const currentIndex = innerGlide.index;
        
        // Remove active class from all bullets
        bullets.forEach(bullet => bullet.classList.remove('active'));
        
        // Add active class to current slide's bullet
        if (bullets[currentIndex]) {
          bullets[currentIndex].classList.add('active');
          debugLog(`Active bullet updated to slide ${currentIndex}`);
        }
      };
      
      // Listen for slide changes
      innerGlide.on('run.after', updateActiveBullet);
      innerGlide.on('mount.after', updateActiveBullet);
      
      // Set initial state
      updateActiveBullet();
      
      debugLog(`Bullet active state sync enabled for inner slider`);
    }
  
    /**
     * Destroy all inner slider instances
     */
    destroyInnerSliders() {
      this.innerGlideInstances.forEach((glideInstance, selector) => {
        try {
          glideInstance.destroy();
          debugLog(`Inner slider destroyed: ${selector}`);
        } catch (error) {
          debugWarn(`Failed to destroy inner slider ${selector}:`, error);
        }
      });
      
      // Clear the map
      this.innerGlideInstances.clear();
    }
  
    playVideo(index) {
      if (this.videos[index]) {
        // Pause all other videos first
        this.pauseAllVideos();
        
        // Play the selected video
        this.videos[index].play().catch(error => {
          debugWarn('Video play failed:', error);
        });
      }
    }
  
    pauseVideo(index) {
      if (this.videos[index]) {
        this.videos[index].pause();
      }
    }
  
    pauseAllVideos() {
      this.videos.forEach(video => {
        if (!video.paused) {
          video.pause();
        }
      });
    }
  
    onVideoPlay(index) {
      // Show pause button, hide play button
      if (this.playButtons[index]) {
        this.playButtons[index].hidden = true;
      }
      if (this.pauseButtons[index]) {
        this.pauseButtons[index].hidden = false;
      }
    }
  
    onVideoPause(index) {
      // Show play button, hide pause button
      if (this.playButtons[index]) {
        this.playButtons[index].hidden = false;
      }
      if (this.pauseButtons[index]) {
        this.pauseButtons[index].hidden = true;
      }
    }
  
    onVideoEnded(index) {
      // Reset to play state when video ends
      this.onVideoPause(index);
    }
  
    // Public methods for external control
    playAllVideos() {
      this.videos.forEach((video, index) => {
        this.playVideo(index);
      });
    }
  
    pauseAllVideos() {
      this.videos.forEach((video, index) => {
        this.pauseVideo(index);
      });
    }
  
    // Additional cleanup method for videos (kept for compatibility)
    cleanupVideoEventListeners() {
      this.pauseAllVideos();
      
      // Remove event listeners
      this.playButtons.forEach(button => {
        button.removeEventListener('click', this.playVideo);
      });
      
      this.pauseButtons.forEach(button => {
        button.removeEventListener('click', this.pauseVideo);
      });
    }
  
    /**
     * Public API Methods for Inner Slider Control
     */
  
    /**
     * Navigate a specific inner slider safely
     * @param {string} selector - The inner images selector (e.g., 'js-sh-before-after-images-slider_0_block-id')
     * @param {string|number} direction - Direction to navigate ('>', '<', '=0', '=1', etc.)
     * @returns {boolean} - True if navigation was successful, false otherwise
     */
    navigateInnerSlider(selector, direction) {
      if (!selector || !direction) {
        debugWarn('navigateInnerSlider: selector and direction are required');
        return false;
      }
  
      const innerSlider = this.innerGlideInstances.get(selector);
      if (!innerSlider) {
        debugWarn(`navigateInnerSlider: Inner slider not found for selector: ${selector}`);
        return false;
      }
  
      try {
        innerSlider.go(direction);
        debugLog(`Inner slider navigation successful: ${selector} -> ${direction}`);
        return true;
      } catch (error) {
        debugError(`navigateInnerSlider: Navigation failed for ${selector}:`, error);
        return false;
      }
    }
  
    /**
     * Get a specific inner slider instance
     * @param {string} selector - The inner images selector
     * @returns {Object|null} - The Glide instance or null if not found
     */
    getInnerSlider(selector) {
      if (!selector) {
        debugWarn('getInnerSlider: selector is required');
        return null;
      }
  
      const innerSlider = this.innerGlideInstances.get(selector);
      if (!innerSlider) {
        debugWarn(`getInnerSlider: Inner slider not found for selector: ${selector}`);
        return null;
      }
  
      return innerSlider;
    }
  
    /**
     * Get all inner slider instances
     * @returns {Map} - Map of all inner slider instances
     */
    getAllInnerSliders() {
      return this.innerGlideInstances;
    }
  
    /**
     * Get the current active slide index for a specific inner slider
     * @param {string} selector - The inner images selector
     * @returns {number|null} - Current slide index or null if not found
     */
    getInnerSliderActiveIndex(selector) {
      const innerSlider = this.getInnerSlider(selector);
      if (!innerSlider) {
        return null;
      }
  
      return innerSlider.index;
    }
  
    /**
     * Go to a specific slide in an inner slider
     * @param {string} selector - The inner images selector
     * @param {number} slideIndex - The slide index to go to (0-based)
     * @returns {boolean} - True if successful, false otherwise
     */
    goToInnerSlide(selector, slideIndex) {
      if (typeof slideIndex !== 'number' || slideIndex < 0) {
        debugWarn('goToInnerSlide: slideIndex must be a non-negative number');
        return false;
      }
  
      return this.navigateInnerSlider(selector, `=${slideIndex}`);
    }
  
    /**
     * Go to next slide in an inner slider
     * @param {string} selector - The inner images selector
     * @returns {boolean} - True if successful, false otherwise
     */
    nextInnerSlide(selector) {
      return this.navigateInnerSlider(selector, '>');
    }
  
    /**
     * Go to previous slide in an inner slider
     * @param {string} selector - The inner images selector
     * @returns {boolean} - True if successful, false otherwise
     */
    prevInnerSlide(selector) {
      return this.navigateInnerSlider(selector, '<');
    }
  
    /**
     * Event-Based Control System
     */
  
    /**
     * Setup event listeners for inner slider control
     */
    setupEventBasedControls() {
      // Listen for inner slider navigation events
      this.addEventListener('inner-slider:navigate', this.handleInnerSliderNavigate.bind(this));
      this.addEventListener('inner-slider:goto', this.handleInnerSliderGoTo.bind(this));
      this.addEventListener('inner-slider:next', this.handleInnerSliderNext.bind(this));
      this.addEventListener('inner-slider:prev', this.handleInnerSliderPrev.bind(this));
    }
  
    /**
     * Handle inner slider navigate event
     * @param {CustomEvent} event - The custom event with detail { selector, direction }
     */
    handleInnerSliderNavigate(event) {
      const { selector, direction } = event.detail || {};
      if (selector && direction) {
        this.navigateInnerSlider(selector, direction);
      } else {
        debugWarn('handleInnerSliderNavigate: Missing selector or direction in event detail');
      }
    }
  
    /**
     * Handle inner slider go to specific slide event
     * @param {CustomEvent} event - The custom event with detail { selector, slideIndex }
     */
    handleInnerSliderGoTo(event) {
      const { selector, slideIndex } = event.detail || {};
      if (selector !== undefined && slideIndex !== undefined) {
        this.goToInnerSlide(selector, slideIndex);
      } else {
        debugWarn('handleInnerSliderGoTo: Missing selector or slideIndex in event detail');
      }
    }
  
    /**
     * Handle inner slider next event
     * @param {CustomEvent} event - The custom event with detail { selector }
     */
    handleInnerSliderNext(event) {
      const { selector } = event.detail || {};
      if (selector) {
        this.nextInnerSlide(selector);
      } else {
        debugWarn('handleInnerSliderNext: Missing selector in event detail');
      }
    }
  
    /**
     * Handle inner slider previous event
     * @param {CustomEvent} event - The custom event with detail { selector }
     */
    handleInnerSliderPrev(event) {
      const { selector } = event.detail || {};
      if (selector) {
        this.prevInnerSlide(selector);
      } else {
        debugWarn('handleInnerSliderPrev: Missing selector in event detail');
      }
    }
  
    /**
     * Utility method to dispatch inner slider events from outside
     * @param {string} eventType - The event type ('navigate', 'goto', 'next', 'prev')
     * @param {Object} detail - The event detail object
     */
    static dispatchInnerSliderEvent(sectionId, eventType, detail) {
      const component = document.querySelector(`sh-before-after[data-section-id="${sectionId}"]`);
      if (component) {
        const event = new CustomEvent(`inner-slider:${eventType}`, { detail });
        component.dispatchEvent(event);
        return true;
      } else {
        debugWarn(`dispatchInnerSliderEvent: Component not found for section ${sectionId}`);
        return false;
      }
    }
  };
  
  // Register the custom element if not already registered
  if (!customElements.get('sh-before-after')) {
    customElements.define('sh-before-after', window.ShBeforeAfter);
  }
  
  } // End of window.ShBeforeAfter check
  
  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    // Auto-initialize any existing sh-before-after elements
    const beforeAfterElements = document.querySelectorAll('sh-before-after');
    beforeAfterElements.forEach(element => {
      if (!element.isInitialized) {
        // Trigger EventBus interaction for initialization
        if (window.EventBus) {
          window.EventBus.emit('page:interaction');
        }
      }
    });
  });
  
  /**
   * USAGE EXAMPLES - Inner Slider Control
   * 
   * Method 1: Direct Component Access
   * ================================
   * 
   * // Get component instance
   * const component = document.querySelector('sh-before-after[data-section-id="123"]');
   * 
   * // Navigate inner slider
   * component.navigateInnerSlider('js-sh-before-after-images-slider_0_block-id', '>'); // Next
   * component.navigateInnerSlider('js-sh-before-after-images-slider_0_block-id', '<'); // Previous
   * component.navigateInnerSlider('js-sh-before-after-images-slider_0_block-id', '=2'); // Go to slide 2
   * 
   * // Convenience methods
   * component.nextInnerSlide('js-sh-before-after-images-slider_0_block-id');
   * component.prevInnerSlide('js-sh-before-after-images-slider_0_block-id');
   * component.goToInnerSlide('js-sh-before-after-images-slider_0_block-id', 1);
   * 
   * // Get slider information
   * const slider = component.getInnerSlider('js-sh-before-after-images-slider_0_block-id');
   * const activeIndex = component.getInnerSliderActiveIndex('js-sh-before-after-images-slider_0_block-id');
   * const allSliders = component.getAllInnerSliders();
   * 
   * Method 2: Event-Based Control
   * ============================
   * 
   * // Dispatch events from anywhere in your code
   * ShBeforeAfter.dispatchInnerSliderEvent('123', 'next', { 
   *   selector: 'js-sh-before-after-images-slider_0_block-id' 
   * });
   * 
   * ShBeforeAfter.dispatchInnerSliderEvent('123', 'prev', { 
   *   selector: 'js-sh-before-after-images-slider_0_block-id' 
   * });
   * 
   * ShBeforeAfter.dispatchInnerSliderEvent('123', 'goto', { 
   *   selector: 'js-sh-before-after-images-slider_0_block-id',
   *   slideIndex: 2 
   * });
   * 
   * ShBeforeAfter.dispatchInnerSliderEvent('123', 'navigate', { 
   *   selector: 'js-sh-before-after-images-slider_0_block-id',
   *   direction: '>' 
   * });
   * 
   * Method 3: Custom Button Integration
   * ==================================
   * 
   * // Add custom buttons to control inner sliders
   * <button onclick="controlInnerSlider('next')">Next Image</button>
   * <button onclick="controlInnerSlider('prev')">Previous Image</button>
   * 
   * function controlInnerSlider(action) {
   *   const sectionId = '123'; // Your section ID
   *   const selector = 'js-sh-before-after-images-slider_0_block-id'; // Your selector
   *   
   *   ShBeforeAfter.dispatchInnerSliderEvent(sectionId, action, { selector });
   * }
   * 
   * Method 4: Advanced Usage with Error Handling
   * ============================================
   * 
   * function safeInnerSliderControl(sectionId, selector, action, slideIndex = null) {
   *   const component = document.querySelector(`sh-before-after[data-section-id="${sectionId}"]`);
   *   
   *   if (!component) {
   *     console.error('Component not found');
   *     return false;
   *   }
   *   
   *   // Check if inner slider exists
   *   const slider = component.getInnerSlider(selector);
   *   if (!slider) {
   *     console.error('Inner slider not found');
   *     return false;
   *   }
   *   
   *   // Perform action
   *   switch (action) {
   *     case 'next':
   *       return component.nextInnerSlide(selector);
   *     case 'prev':
   *       return component.prevInnerSlide(selector);
   *     case 'goto':
   *       return component.goToInnerSlide(selector, slideIndex);
   *     default:
   *       console.error('Unknown action');
   *       return false;
   *   }
   * }
   * 
   * IMPORTANT NOTES:
   * ===============
   * - Inner slider selectors follow pattern: 'js-sh-before-after-images-slider_{slideIndex}_{blockId}'
   * - All methods include error handling and validation
   * - Events are isolated - inner slider navigation won't affect outer slider
   * - Use browser console to see success/error messages
   * - Component must be initialized before calling methods
   */
  